import React, { useCallback, useEffect, useState } from 'react'
import { NEXT_IMAGE_QUALITY, VIDEO_RATIO } from '~constants'
import Image from 'next/image'
import styles from './IframeVideo.module.scss'
import { JsonLD } from '~elements'
import { useWindowSize } from 'utils/hooks'
import { useSmoothAnchorScroll } from 'utils/hooks/useSmoothAnchorScroll'
import { getImageUrl } from 'utils/assets/getImageUrl'

interface IframeVideoProps {
  width: number
  height: number
  url: string
  title: string
  thumbnailUrl?: string
  uploadDate?: string
  endDate?: string
  anchorId: string
  posterImage: {
    posterImageXl?: { url: string; altText?: string }
    posterImageMd?: { url: string; altText?: string }
    posterImageSm?: { url: string; altText?: string }
  }
  isLiveStreamOn: boolean
}

const IframeVideo = ({
  height,
  url,
  title,
  uploadDate,
  endDate,
  thumbnailUrl,
  posterImage,
  isLiveStreamOn,
  anchorId
}: IframeVideoProps) => {
  const [videoHeight, setVideoHeight] = useState<number>()
  const modalPaddingOffset = 60

  const handleChangeVideoWidth = useCallback(() => {
    const height = (window.innerWidth - modalPaddingOffset) * VIDEO_RATIO
    return setVideoHeight(height)
  }, [])

  const { posterImageXl, posterImageMd, posterImageSm } = posterImage
  const { isBreakpoint: isAboveBreakpointSm } = useWindowSize('sm', '>')
  const { isBreakpoint: isAboveBreakpointLg } = useWindowSize('lg', '>')
  const { ref: containerRef, shouldSetId } = useSmoothAnchorScroll(anchorId)
  const selectedPosterImage = isAboveBreakpointLg
    ? { imageUrl: posterImageXl?.url, altText: posterImageXl?.altText }
    : isAboveBreakpointSm
    ? { imageUrl: posterImageMd?.url, altText: posterImageMd?.altText }
    : { imageUrl: posterImageSm?.url, altText: posterImageSm?.altText }
  const posterImageUrl = getImageUrl(selectedPosterImage)
  const posterImageAlt = selectedPosterImage?.altText || ''

  useEffect(() => {
    window.addEventListener('resize', handleChangeVideoWidth)
    const height = (window.innerWidth - modalPaddingOffset) * VIDEO_RATIO
    setVideoHeight(height)

    return () => {
      window.removeEventListener('resize', handleChangeVideoWidth)
    }
  }, [videoHeight, handleChangeVideoWidth])

  const structuredData = {
    '@context': 'http://schema.org',
    '@type': 'VideoObject',
    name: title,
    thumbnailUrl: thumbnailUrl,
    uploadDate: uploadDate,
    contentUrl: url,
    publication: {
      '@type': 'BroadcastEvent',
      name: title,
      isLiveBroadcast: true,
      startDate: uploadDate,
      endDate: endDate
    }
  }
  return (
    <>
      <JsonLD data={structuredData} />
      <div className={styles['container']} {...(shouldSetId && { id: anchorId })} ref={containerRef}>
        <div>
          {url && isLiveStreamOn ? (
            <div className={styles['iframe-container']}>
              <iframe
                width='100%'
                height={height}
                src={url}
                title={title}
                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                allowFullScreen
              />
            </div>
          ) : posterImageUrl ? (
            <div className={styles['poster-image']}>
              <div className={'image-container'}>
                <Image
                  alt={posterImageAlt}
                  src={posterImageUrl}
                  width='0'
                  height='0'
                  sizes='100vw'
                  quality={NEXT_IMAGE_QUALITY}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}

export default IframeVideo
